import { Injectable, inject } from '@angular/core';
import { Action, Selector, State, StateContext } from '@ngxs/store';
import { switchMap } from 'rxjs';
import { delay } from 'rxjs/operators';
import { Country } from '../../core.types';
import { CountriesService } from '../../services/countries.service';
import {
  ActivateCountryAction,
  ClearAccountManagerCountriesAction,
  CreateCountryAction,
  DeactivateCountryAction,
  GetAccountManagerCountriesAction,
  GetCountriesAction,
  GetCountryAction,
  SetCountriesAction,
  UpdateCountryAction,
} from './countries.actions';

export interface CountriesStateModel {
  countries: Country[];
  accountManagerCountries: string[];
}

@State<CountriesStateModel>({
  name: 'countries',
  defaults: {
    countries: [],
    accountManagerCountries: [],
  },
})
@Injectable()
export class CountriesState {
  private countriesService = inject(CountriesService);

  @Selector()
  static getAccountManagerCountries(state: CountriesStateModel) {
    return state.accountManagerCountries;
  }

  @Selector()
  static getCountries(state: CountriesStateModel) {
    return state.countries;
  }

  @Selector()
  static getCountryById(state: CountriesStateModel) {
    return (id) => state.countries.find((country) => country.id === id);
  }

  @Selector([CountriesState.getCountries])
  static getCountryByName(countries: Country[]) {
    return (name: string) => countries.find((country) => country.name === name);
  }

  @Selector([CountriesState.getCountries])
  static getCountryByIso(countries) {
    return (iso3) => countries.find((country) => country.isoCode3 === iso3);
  }

  @Selector([CountriesState.getCountryByName])
  static getCountryRegions(countryByName) {
    return (name) => countryByName(name).regions;
  }

  @Action(SetCountriesAction)
  setCountries(
    { patchState, getState }: StateContext<CountriesStateModel>,
    { countries }: SetCountriesAction
  ) {
    const filteredCountries = [...getState().countries].filter(
      (country) => !countries.find((newCountry) => newCountry.id === country.id)
    );
    return patchState({
      countries: filteredCountries
        .concat(countries)
        .sort((c1, c2) => c1.name.localeCompare(c2.name)),
    });
  }

  @Action(GetCountriesAction)
  getCountriesList({ dispatch }: StateContext<CountriesStateModel>) {
    return this.countriesService
      .getCountries()
      .pipe(
        switchMap((countries) => dispatch(new SetCountriesAction(countries)))
      );
  }

  @Action(GetAccountManagerCountriesAction)
  getAccountManagerCountries(
    { patchState }: StateContext<CountriesStateModel>,
    { ids }: GetAccountManagerCountriesAction
  ) {
    return this.countriesService
      .getAccountManagerCountriesAction(ids.join(','))
      .subscribe((accountManagerCountries) => {
        patchState({ accountManagerCountries });
      });
  }

  @Action(ClearAccountManagerCountriesAction)
  clearAccountManagerCountriesAction({
    patchState,
  }: StateContext<CountriesStateModel>) {
    patchState({ accountManagerCountries: null });
  }

  @Action(GetCountryAction)
  getCountry(
    { dispatch }: StateContext<CountriesStateModel>,
    { id }: GetCountryAction
  ) {
    return this.countriesService
      .getCountry(id)
      .pipe(
        switchMap((country) => dispatch(new SetCountriesAction([country])))
      );
  }

  @Action(UpdateCountryAction)
  updateCountry(
    { dispatch }: StateContext<CountriesStateModel>,
    { id, country }: UpdateCountryAction
  ) {
    return this.countriesService.updateCountry(id, country).pipe(
      delay(500),
      switchMap(() => dispatch(new GetCountryAction(id)))
    );
  }

  @Action(CreateCountryAction)
  createCountry(
    { dispatch }: StateContext<CountriesStateModel>,
    { country }: CreateCountryAction
  ) {
    return this.countriesService.createCountry(country).pipe(
      delay(500),
      switchMap(({ id }) => dispatch(new GetCountryAction(id)))
    );
  }

  @Action(ActivateCountryAction)
  activateCountry(
    {}: StateContext<CountriesStateModel>,
    { id }: ActivateCountryAction
  ) {
    return this.countriesService.activateCountry(id);
  }

  @Action(DeactivateCountryAction)
  deactivateCountry(
    {}: StateContext<CountriesStateModel>,
    { id }: DeactivateCountryAction
  ) {
    return this.countriesService.deactivateCountry(id);
  }
}
